import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { API, CONTACT_MAX_NOTES_LEN, genderOptions, TrackerDataType } from '../../../shared/constants/constants';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import { openSnackbar } from '../../AppSnackbar/snackbarSlice';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import AddIcon from '@mui/icons-material/Add';
import MergeIcon from '@mui/icons-material/Merge';
import ArchiveIcon from '@mui/icons-material/Archive';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import MergeContactPersonnelModal from './MergeContactPersonnelModal';
import AlertChangeOrgUnit from '../AlertChangeOrgUnit';
import { normalizeCommunityMemberPayload } from '../../../shared/utils/communityMemberNormalization';
import {
  getCommunityMembersByCaseInsensitiveNameAndOrganization,
  getExpertsByCaseInsensitiveNameAndUnit,
  getJournalistsByCaseInsensitiveNameAndOrg,
} from '../contactsRequest';
import { useArchiveContact } from '../Hooks/useArchiveContact';
import { ComboBoxCreatableOutlet } from '../../Cells/CellTypes';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { normalizeExpertPayload } from '../../../utils/expertNormalization';
import { normalizeJournalistPayload } from '../../../shared/functions/normalizeJournalistPayload';
import {
  adderHeaderTitleSx,
  queryAdderPopupFieldLabelSx,
  queryAdderPopupInputSx,
  queryAdderPopupFormSpacing,
  utilityModalContentSx,
  utilityModalDialogSx,
  utilityModalFooterSx,
  utilityModalHeaderSx,
  utilityModalIconTileSx,
  utilityModalPaperSx,
  utilityModalTitleSx,
  utilityCancelButtonSx,
  utilityPrimaryActionButtonSx,
} from '../../QueryAdder/queryAdderPopupStyles';

const contactModalCellInputSx = {
  display: 'flex',
  flexDirection: 'column',
};

const contactModalCharCountTextSx = {
  color: 'rgba(0, 0, 0, 0.6)',
};

const contactFieldLabelSx = {
  ...queryAdderPopupFieldLabelSx,
  fontWeight: 500,
};

const contactPopupInputSx = queryAdderPopupInputSx;

const getCurrentOrg = (orgs, selectedOrg) => {
  if (!selectedOrg) {
    return undefined;
  }
  return orgs.find((org) => org._id === selectedOrg);
};

const getSelectedExpertUnits = (selectedContact, units) => {
  if (!selectedContact?.units || !Array.isArray(units)) {
    return [];
  }
  return units.filter((unit) => selectedContact.units.includes(unit._id));
};

const getPrefillOrgUnit = ({ isEditing, category, selectedContact, orgs, units }) => {
  if (!isEditing) {
    return category === 'Experts' ? [] : undefined;
  }

  switch (category) {
    case 'Journalists':
      return orgs.find((org) => org._id === selectedContact?.Org?._id);
    case 'Community Members':
      return orgs.find((org) => org._id === selectedContact?.Organization?._id);
    case 'Experts':
      return getSelectedExpertUnits(selectedContact, units);
    default:
      return units.find((unit) => unit._id === selectedContact?.unit);
  }
};

const concatCustomFieldsYupSchema = (baseSchema, customFieldConfigs) => {
  if (!customFieldConfigs) {
    return baseSchema;
  }

  const customFields = {};
  customFieldConfigs.forEach((config) => {
    customFields[config.fieldKey] = yup.string().nullable();
  });

  return baseSchema.concat(yup.object().shape(customFields));
};

const getCategorySchemaConfig = (category, customFieldConfigs) => {
  let schema;
  let url;

  switch (category) {
    case 'Journalists':
      url = '/journalist';
      schema = yup.object().shape({
        name: yup.string().required(),
        email: yup.string().email(),
        phone: yup.string().max(32),
        cell: yup.string().max(32),
        notes: yup.string().min(0).max(CONTACT_MAX_NOTES_LEN),
        Org: yup.string().nullable(),
      });
      break;
    case 'Community Members':
      url = '/community-member';
      schema = yup.object().shape({
        name: yup.string().required(),
        email: yup.string().email(),
        phone: yup.string().max(32),
        cell: yup.string().max(32),
        notes: yup.string().min(0).max(CONTACT_MAX_NOTES_LEN),
        Organization: yup.string().nullable(),
      });
      break;
    default:
      url = '/expert';
      schema = yup.object().shape({
        name: yup.string().required(),
        email: yup.string().email(),
        phone: yup.string().max(32),
        units: yup.array().of(
          yup.lazy((value) => {
            if (typeof value === 'string') {
              return yup.string().required();
            }
            return yup.object().shape({
              name: yup.string().required(),
            });
          })
        ),
        language: yup.string(),
        gender: yup.string().nullable(),
        notes: yup.string().min(0).max(CONTACT_MAX_NOTES_LEN),
      });
      break;
  }

  return {
    schema: concatCustomFieldsYupSchema(schema, customFieldConfigs),
    url,
  };
};

const normalizeContactData = (category, data) => {
  switch (category) {
    case 'Community Members':
      return normalizeCommunityMemberPayload(data);
    case 'Journalists':
      return normalizeJournalistPayload(data);
    default:
      return data;
  }
};

const getDialogValueReset = (category) => {
  switch (category) {
    case 'Experts':
      return {
        name: '',
        phone: '',
        email: '',
        unit: '',
        language: '',
        gender: '',
        _id: '',
      };
    case 'Community Members':
      return {
        name: '',
        phone: '',
        cell: '',
        notes: '',
        email: '',
        Organization: '',
        _id: '',
      };
    default:
      return {
        name: '',
        phone: '',
        cell: '',
        notes: '',
        email: '',
        Org: '',
        _id: '',
      };
  }
};

const getModalTitle = (category, isEditing) => {
  const currentCategory = category.slice(0, -1);
  return isEditing ? `Edit ${currentCategory}` : `New ${currentCategory}`;
};

const getDefaultPopupType = (category) => {
  switch (category) {
    case 'Journalists':
      return TrackerDataType.MEDIA_INTERACTION;
    case 'Community Members':
      return TrackerDataType.ENGAGEMENT;
    default:
      return TrackerDataType.SERVICE;
  }
};

const getDuplicateWarningMessage = (count, category) => {
  return `${count} contact personnel(s) with the same Name and Organization/Unit already exists. Please check if you are trying to add a ${category
    .toLowerCase()
    .slice(0, -1)} that already exists.`;
};

const getDuplicateQueryPromise = (category, params) => {
  switch (category) {
    case 'Journalists':
      return getJournalistsByCaseInsensitiveNameAndOrg(params.name, params.Org ? params.Org : '');
    case 'Community Members':
      return getCommunityMembersByCaseInsensitiveNameAndOrganization(
        params.name,
        params.Organization ? params.Organization : ''
      );
    default:
      return getExpertsByCaseInsensitiveNameAndUnit(params.name, params.unit ? params.unit : '');
  }
};

const updateLinkedContactValue = ({
  category,
  response,
  variables,
  dialogValue,
  value,
  setValue,
  callback,
  col,
  currentOrg,
  prefilledOrgUnit,
}) => {
  const createdName = response.data.name ?? variables?.name ?? dialogValue?.name ?? '';
  dialogValue._id = response.data.id;

  switch (category) {
    case 'Experts':
      dialogValue.Org = response.data.Org;
      value.push({ _id: response.data.id, name: createdName });
      callback(value, col);
      return;
    case 'Community Members': {
      const Organization = (currentOrg || prefilledOrgUnit)?._id ?? '';
      dialogValue.Organization = Organization;
      const createdContact = { _id: response.data.id, name: createdName, Organization };
      setValue(createdContact);
      callback({ ...dialogValue, name: createdName }, col);
      return;
    }
    default: {
      const Org = (currentOrg || prefilledOrgUnit)?._id ?? '';
      dialogValue.Org = Org;
      const createdContact = { _id: response.data.id, name: createdName, Org };
      setValue(createdContact);
      callback({ ...dialogValue, name: createdName }, col);
    }
  }
};

const CategorySpecificFields = ({
  category,
  isInContactPage,
  control,
  currentOrg,
  prefilledOrgUnit,
  orgs,
  units,
  errors,
  isEditing,
  selectedContact,
  setPrefilledOrgUnit,
  setRefreshOrgs,
  setChangeWarning,
  renderCustomFields,
  popupType,
}) => {
  const safeOrgs = Array.isArray(orgs) ? orgs : [];
  const safeUnits = Array.isArray(units) ? units : [];
  const expertUnitsValue = Array.isArray(prefilledOrgUnit) ? prefilledOrgUnit : [];

  const renderCellField = () => (
    <Controller
      name="cell"
      control={control}
      defaultValue={isEditing ? selectedContact.cell : ''}
      render={({ field }) => (
        <Box>
          <Typography sx={contactFieldLabelSx}>Cell</Typography>
          <TextField
            {...field}
            variant="outlined"
            sx={[contactModalCellInputSx, contactPopupInputSx]}
            error={errors.phone}
            helperText={errors.phone?.message}
            data-cy="cell-input-CombinedContactPersonnelModal"
          />
        </Box>
      )}
    />
  );

  const renderJournalistOrgField = () => {
    if (isInContactPage) {
      return (
        <Box>
          <Typography sx={contactFieldLabelSx}>Organization</Typography>
          <Controller
            name="Org"
            control={control}
            defaultValue={currentOrg ? currentOrg._id : prefilledOrgUnit?._id}
            render={({ field }) => (
              <ComboBoxCreatableOutlet
                options={safeOrgs}
                val={currentOrg ? currentOrg._id : prefilledOrgUnit?._id}
                col={'outlet'}
                textFieldVariant={'outlined'}
                inputSx={contactPopupInputSx}
                disableTooltip={true}
                popupType={popupType}
                callback={(data) => {
                  setPrefilledOrgUnit(data);
                  field.onChange(data?._id);
                  setRefreshOrgs();
                  if (isEditing) setChangeWarning(true);
                }}
              />
            )}
          />
        </Box>
      );
    }

    return (
      <Box>
        <Typography sx={contactFieldLabelSx}>Organization</Typography>
        <Controller
          name="Org"
          control={control}
          defaultValue={currentOrg ? currentOrg._id : prefilledOrgUnit?._id}
          render={({ field }) => (
            <Autocomplete
              {...field}
              disablePortal
              clearOnEscape
              openOnFocus
              autoHighlight
              options={safeOrgs}
              getOptionLabel={(option) => option.name}
              value={currentOrg || prefilledOrgUnit}
              onChange={(_, data) => {
                setPrefilledOrgUnit(data);
                field.onChange(data ? data._id : data);
                if (isEditing) setChangeWarning(true);
              }}
              data-cy="org-autoComplete-CombinedContactPersonnelModal"
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  sx={[contactModalCellInputSx, contactPopupInputSx]}
                  error={errors.Org}
                  helperText={errors.Org?.message}
                  data-cy="org-input-CombinedContactPersonnelModal"
                />
              )}
            />
          )}
        />
      </Box>
    );
  };

  switch (category) {
    case 'Journalists':
      return (
        <>
          {renderJournalistOrgField()}
          {renderCellField()}
          {renderCustomFields()}
        </>
      );
    case 'Experts':
      return (
        <>
          <Box>
            <Typography sx={contactFieldLabelSx}>Units</Typography>
            <Controller
              name="units"
              control={control}
              defaultValue={isEditing ? prefilledOrgUnit : []}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  multiple
                  disablePortal
                  clearOnEscape
                  openOnFocus
                  autoHighlight
                  options={safeUnits}
                  getOptionLabel={(option) => option.name}
                  value={expertUnitsValue}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      return (
                        <Chip
                          sx={{ 'MuiChip-label': { fontSize: '12px' } }}
                          size="small"
                          variant="outlined"
                          label={option.name}
                          {...getTagProps({ index })}
                        />
                      );
                    })
                  }
                  onChange={(_, data) => {
                    setPrefilledOrgUnit(data || []);
                    field.onChange(data ? data.map((d) => d._id) : []);
                    if (isEditing) setChangeWarning(true);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      sx={[contactModalCellInputSx, contactPopupInputSx]}
                      error={errors.unit}
                      helperText={errors.unit?.message}
                      data-cy="unit-input-CombinedContactPersonnelModal"
                    />
                  )}
                />
              )}
            />
          </Box>
          <Controller
            name="language"
            control={control}
            defaultValue={isEditing ? selectedContact.language : ''}
            render={({ field }) => (
              <Box>
                <Typography sx={contactFieldLabelSx}>Language</Typography>
                <TextField
                  {...field}
                  variant="outlined"
                  sx={[contactModalCellInputSx, contactPopupInputSx]}
                  error={errors.language}
                  helperText={errors.language?.message}
                  data-cy="language-input-CombinedContactPersonnelModal"
                />
              </Box>
            )}
          />
          <Box>
            <Typography sx={contactFieldLabelSx}>Gender</Typography>
            <Controller
              name="gender"
              control={control}
              defaultValue={isEditing ? selectedContact?.gender : ''}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  disablePortal
                  clearOnEscape
                  openOnFocus
                  autoHighlight
                  options={genderOptions}
                  getOptionLabel={(option) => option.name ?? option}
                  isOptionEqualToValue={(option, value) => option.name === value}
                  onChange={(_, data) => {
                    field.onChange(data ? data.name : data);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      sx={[contactModalCellInputSx, contactPopupInputSx]}
                      error={errors.gender}
                      helperText={errors.gender?.message}
                      data-cy="gender-input-CombinedContactPersonnelModal"
                    />
                  )}
                />
              )}
            />
          </Box>
          {renderCustomFields()}
        </>
      );
    default:
      return (
        <>
          <Box>
            <Typography sx={contactFieldLabelSx}>Organization</Typography>
            <Controller
              name="Organization"
              control={control}
              defaultValue={currentOrg ? currentOrg._id : prefilledOrgUnit?._id}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  disablePortal
                  clearOnEscape
                  openOnFocus
                  autoHighlight
                  options={safeOrgs}
                  getOptionLabel={(option) => option.name}
                  value={currentOrg || prefilledOrgUnit}
                  onChange={(_, data) => {
                    setPrefilledOrgUnit(data);
                    field.onChange(data ? data._id : data);
                    if (isEditing) setChangeWarning(true);
                  }}
                  data-cy="organization-autoComplete-CombinedContactPersonnelModal"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      sx={[contactModalCellInputSx, contactPopupInputSx]}
                      error={errors.Organization}
                      helperText={errors.Organization?.message}
                      data-cy="organization-input-CombinedContactPersonnelModal"
                    />
                  )}
                />
              )}
            />
          </Box>
          {renderCellField()}
          {renderCustomFields()}
        </>
      );
  }
};

CategorySpecificFields.propTypes = {
  category: PropTypes.oneOf(['Journalists', 'Community Members', 'Experts']).isRequired,
  isInContactPage: PropTypes.bool,
  control: PropTypes.object.isRequired,
  currentOrg: PropTypes.shape({
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  }),
  prefilledOrgUnit: PropTypes.oneOfType([
    PropTypes.shape({
      _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
      })
    ),
  ]),
  orgs: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    })
  ),
  units: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    })
  ),
  errors: PropTypes.object.isRequired,
  isEditing: PropTypes.bool,
  selectedContact: PropTypes.shape({
    cell: PropTypes.string,
    language: PropTypes.string,
    gender: PropTypes.string,
  }),
  setPrefilledOrgUnit: PropTypes.func.isRequired,
  setRefreshOrgs: PropTypes.func,
  setChangeWarning: PropTypes.func.isRequired,
  renderCustomFields: PropTypes.func.isRequired,
  popupType: PropTypes.oneOf(Object.values(TrackerDataType)),
};

const CombinedContactPersonnelModal = (props) => {
  // Props for adding a new contact
  const {
    openAddContact,
    setOpenAddContact,
    orgs,
    setRefreshContacts,
    setRefreshOrgs,
    category,
    units,
    customFieldConfigs,
  } = props;
  // Props for editing a contact on Contacts page
  const { isEditing, selectedContact } = props;
  // Props for entering from MediaInteractions/ServicePage/EngagementLog
  const { isInContactPage, col, dialogValue, setDialogValue, value, setValue, callback, selectedOrg, popupType } =
    props;
  // Props for entering from AutoTracker
  const { isInAutoTrackerPage } = props;
  const resolvedPopupType = popupType ?? getDefaultPopupType(category);

  const currentOrg = getCurrentOrg(orgs, selectedOrg);

  // Prop for merging contacts
  const { contacts } = props;

  const [prefilledOrgUnit, setPrefilledOrgUnit] = React.useState(() =>
    getPrefillOrgUnit({ isEditing, category, selectedContact, orgs, units })
  );

  const [openMergeContact, setOpenMergeContact] = React.useState(false);
  const [changeWarning, setChangeWarning] = React.useState(false);

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const archiveContactMutation = useArchiveContact({
    contactId: selectedContact?._id,
    category: category,
    isArchiving: true,
    setRefetchContacts: setRefreshContacts,
  });

  const duplicateCheckParamsRef = React.useRef(null);

  const duplicateCheckQuery = useQuery({
    queryKey: ['contactPersonnelDuplicates', category, selectedContact?._id],
    queryFn: async () => {
      const params = duplicateCheckParamsRef.current;
      if (!params) {
        return { data: [] };
      }
      return getDuplicateQueryPromise(category, params);
    },
    enabled: false,
    staleTime: 0,
  });

  const createContactMutation = useMutation({
    mutationFn: (data) => {
      const payload = category === 'Experts' ? normalizeExpertPayload(data) : data;
      return API.post(url, payload);
    },
    onSuccess: async (response, variables) => {
      if (response.status !== 201) {
        alert(response.statusText);
        return;
      }
      dispatch(openSnackbar({ severity: 'success', message: 'Contact Personnel Created!' }));
      if (isInContactPage) {
        setRefreshContacts(true);
      }
      await queryClient.invalidateQueries({ queryKey: ['dropdown-table'] });

      if (!isInContactPage && !isInAutoTrackerPage) {
        updateLinkedContactValue({
          category,
          response,
          variables,
          dialogValue,
          value,
          setValue,
          callback,
          col,
          currentOrg,
          prefilledOrgUnit,
        });
      } else if (isInAutoTrackerPage) {
        callback(dialogValue, col);
      }
    },
    onError: (error) => {
      console.log(JSON.stringify(error));
      alert(`post failed - ${error}`);
    },
  });

  const updateContactMutation = useMutation({
    mutationFn: (data) => {
      const payload = category === 'Experts' ? normalizeExpertPayload(data) : data;
      return API.put(`${url}/${selectedContact._id}`, payload);
    },
    onSuccess: async (response) => {
      if (response.status !== 200) {
        alert(response.statusText);
        return;
      }
      dispatch(openSnackbar({ severity: 'success', message: 'Contact Personnel Updated!' }));
      setRefreshContacts(true);
      await queryClient.invalidateQueries({ queryKey: ['dropdown-table'] });
      handleClose();
    },
    onError: (error) => {
      alert(`put failed - ${error}`);
    },
  });

  const { schema, url } = getCategorySchemaConfig(category, customFieldConfigs);

  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      email: null,
      phone: null,
      Org: null,
      Organization: null,
      unit: null,
      units: [],
      cell: null,
      notes: null,
      language: null,
      gender: null,
    },
  });
  const notesValue = watch('notes') ?? '';

  const checkDuplicates = async (data) => {
    try {
      duplicateCheckParamsRef.current = data;
      const { data: response } = await duplicateCheckQuery.refetch();
      const responseData = Array.isArray(response?.data) ? response.data : [];

      if (responseData.length > 0) {
        dispatch(
          openSnackbar({
            severity: 'warning',
            message: getDuplicateWarningMessage(responseData.length, category),
          })
        );
        return true;
      }
      return false;
    } catch (err) {
      alert(err);
      return false;
    }
  };

  const checkDuplicatesButExcludeSelected = async (data) => {
    try {
      duplicateCheckParamsRef.current = data;
      const { data: response } = await duplicateCheckQuery.refetch();
      const responseData = Array.isArray(response?.data) ? response.data : [];
      const filteredDuplicates = responseData.filter((oneDuplicateContact) => {
        return oneDuplicateContact._id !== selectedContact._id;
      });

      if (filteredDuplicates.length > 0) {
        dispatch(
          openSnackbar({
            severity: 'warning',
            message: getDuplicateWarningMessage(filteredDuplicates.length, category),
          })
        );
        return true;
      }
      return false;
    } catch (err) {
      alert(err);
      return false;
    }
  };

  const handleArchive = async () => {
    try {
      await archiveContactMutation.mutateAsync();
      handleClose();
    } catch (error) {
      console.log(error);
    }
  };

  const handleUpdate = async (data) => {
    const normalizedData = normalizeContactData(category, data);

    if (await checkDuplicatesButExcludeSelected(normalizedData)) return;

    return updateContactMutation.mutateAsync(normalizedData);
  };

  const handleClose = () => {
    reset();
    setOpenAddContact(false);
    setChangeWarning(false);
    if (!isInContactPage) {
      setDialogValue(getDialogValueReset(category));
    }
  };

  const forSubmit = async (data, category) => {
    const normalizedData = normalizeContactData(category, data);
    if (isEditing) {
      await handleUpdate(normalizedData);
      return;
    }
    // first, check if the contact with the same name and Org already exists
    if (await checkDuplicates(normalizedData)) return;

    // if not, create a new contact
    createContactMutation.mutate(normalizedData);
    reset();
    handleClose();
  };

  useEffect(() => {
    reset({
      name: selectedContact?.name,
      email: selectedContact?.email,
      phone: selectedContact?.phone,
    });
    if (selectedContact) {
      setPrefilledOrgUnit(getPrefillOrgUnit({ isEditing, category, selectedContact, orgs, units }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, selectedContact, category, units, reset]);

  useEffect(() => {
    if (!isInContactPage) {
      reset({
        name: dialogValue.name,
      });
    }
  }, [dialogValue?.name, isInContactPage, reset]);

  const renderCustomFields = () => {
    if (customFieldConfigs) {
      return (
        <>
          {customFieldConfigs.map((customFieldConfig) => (
            <Controller
              key={customFieldConfig.fieldKey}
              name={customFieldConfig.fieldKey}
              control={control}
              defaultValue={
                selectedContact?.[customFieldConfig.fieldKey] ? selectedContact[customFieldConfig.fieldKey] : ''
              }
              render={({ field }) => (
                <Box>
                  <Typography sx={contactFieldLabelSx}>{customFieldConfig.label}</Typography>
                  <TextField
                    {...field}
                    data-cy={`combined-personnel-modal-textfield-custom-field-${customFieldConfig.label}`}
                    variant="outlined"
                    sx={[contactModalCellInputSx, contactPopupInputSx]}
                  />
                </Box>
              )}
            />
          ))}
        </>
      );
    }
    return null;
  };

  const handleFormSubmit = (event) => {
    if (event) {
      if (typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
      if (typeof event.stopPropagation === 'function') {
        event.stopPropagation();
      }
    }
    return handleSubmit((data) => forSubmit(data, category))(event);
  };

  return (
    <Dialog
      open={openAddContact}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={utilityModalDialogSx}
      PaperProps={{
        sx: utilityModalPaperSx,
      }}
    >
      <form
        onSubmit={handleFormSubmit}
        data-cy="form-CombinedContactPersonnelModal"
        style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}
      >
        <DialogTitle sx={utilityModalHeaderSx}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={utilityModalIconTileSx}>
              <PersonAddAlt1Icon sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  ...utilityModalTitleSx,
                  ...adderHeaderTitleSx,
                  whiteSpace: 'normal',
                  overflow: 'visible',
                  textOverflow: 'unset',
                }}
              >
                {getModalTitle(category, isEditing)}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ ...utilityModalContentSx, pt: 4.5, pb: 1.5 }}>
          <Stack
            spacing={queryAdderPopupFormSpacing}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              pt: 0.5,
            }}
          >
            <Controller
              name="name"
              control={control}
              defaultValue={() => {
                if (isEditing) return selectedContact.name;
                if (!isInContactPage) return dialogValue.name;
                return '';
              }}
              render={({ field }) => (
                <Box>
                  <Typography sx={contactFieldLabelSx}>Name</Typography>
                  <TextField
                    {...field}
                    variant="outlined"
                    sx={[contactModalCellInputSx, contactPopupInputSx]}
                    error={!!errors.name}
                    helperText={errors.name ? errors.name.message : ''}
                    data-cy="name-input-CombinedContactPersonnelModal"
                  />
                </Box>
              )}
            />

            <Controller
              name="email"
              control={control}
              defaultValue={isEditing ? selectedContact.email : ''}
              render={({ field }) => (
                <Box>
                  <Typography sx={contactFieldLabelSx}>Email</Typography>
                  <TextField
                    {...field}
                    variant="outlined"
                    sx={[contactModalCellInputSx, contactPopupInputSx]}
                    error={errors.email}
                    helperText={errors.email?.message}
                    data-cy="email-input-CombinedContactPersonnelModal"
                  />
                </Box>
              )}
            />

            <Controller
              name="phone"
              control={control}
              defaultValue={isEditing ? selectedContact.phone : ''}
              render={({ field }) => (
                <Box>
                  <Typography sx={contactFieldLabelSx}>Phone</Typography>
                  <TextField
                    {...field}
                    variant="outlined"
                    sx={[contactModalCellInputSx, contactPopupInputSx]}
                    error={errors.phone}
                    helperText={errors.phone?.message}
                    data-cy="phone-input-CombinedContactPersonnelModal"
                  />
                </Box>
              )}
            />

            <CategorySpecificFields
              category={category}
              isInContactPage={isInContactPage}
              control={control}
              currentOrg={currentOrg}
              prefilledOrgUnit={prefilledOrgUnit}
              orgs={orgs}
              units={units}
              errors={errors}
              isEditing={isEditing}
              selectedContact={selectedContact}
              setPrefilledOrgUnit={setPrefilledOrgUnit}
              setRefreshOrgs={setRefreshOrgs}
              setChangeWarning={setChangeWarning}
              renderCustomFields={renderCustomFields}
              popupType={resolvedPopupType}
            />

            <Controller
              name="notes"
              control={control}
              defaultValue={isEditing ? selectedContact.notes : ''}
              render={({ field }) => (
                <Box>
                  <Typography sx={contactFieldLabelSx}>Notes</Typography>
                  <TextField
                    {...field}
                    multiline
                    minRows={2}
                    maxRows={5}
                    variant="outlined"
                    inputProps={{ maxLength: CONTACT_MAX_NOTES_LEN }}
                    helperText={
                      <Box
                        sx={contactModalCharCountTextSx}
                      >{`${notesValue.length}/${CONTACT_MAX_NOTES_LEN} character(s)`}</Box>
                    }
                    sx={[contactModalCellInputSx, contactPopupInputSx]}
                    error={errors.notes}
                    data-cy="notes-input-CombinedContactPersonnelModal"
                  />
                </Box>
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ ...utilityModalFooterSx, justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
          <Button
            onClick={handleClose}
            data-cy="cancel-button-CombinedContactPersonnelModal"
            variant="outlined"
            sx={utilityCancelButtonSx}
          >
            Cancel
          </Button>
          {isEditing && (
            <Button
              onClick={() => setOpenMergeContact(true)}
              startIcon={<MergeIcon />}
              data-cy="merge-button-CombinedContactPersonnelModal"
              variant="outlined"
              sx={utilityCancelButtonSx}
            >
              Merge
            </Button>
          )}
          {isEditing && (
            <Button
              onClick={() => handleArchive()}
              startIcon={<ArchiveIcon />}
              data-cy="archive-button-CombinedContactPersonnelModal"
              variant="outlined"
              sx={utilityCancelButtonSx}
            >
              Archive
            </Button>
          )}
          {isEditing && (
            <Button
              type="submit"
              startIcon={<SyncAltIcon />}
              data-cy="update-button-CombinedContactPersonnelModal"
              variant="contained"
              sx={utilityPrimaryActionButtonSx}
            >
              Update
            </Button>
          )}
          {!isEditing && (
            <Button
              type="submit"
              startIcon={<AddIcon />}
              data-cy="add-button-CombinedContactPersonnelModal"
              variant="contained"
              sx={utilityPrimaryActionButtonSx}
            >
              Add
            </Button>
          )}
        </DialogActions>
      </form>

      <MergeContactPersonnelModal
        openMergeContact={openMergeContact}
        setOpenMergeContact={setOpenMergeContact}
        selectedContact={selectedContact}
        category={category}
        contacts={contacts}
        orgs={orgs}
        units={units}
        setRefreshContacts={setRefreshContacts}
        setOpenAddContact={setOpenAddContact}
      />
      <AlertChangeOrgUnit open={changeWarning} setOpen={setChangeWarning} category={category} />
    </Dialog>
  );
};

export default CombinedContactPersonnelModal;

CombinedContactPersonnelModal.propTypes = {
  openAddContact: PropTypes.bool,
  setOpenAddContact: PropTypes.func,
  orgs: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    })
  ),
  setRefreshContacts: PropTypes.func,
  setRefreshOrgs: PropTypes.func,
  category: PropTypes.oneOf(['Journalists', 'Community Members', 'Experts']).isRequired,
  units: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    })
  ),
  customFieldConfigs: PropTypes.arrayOf(
    PropTypes.shape({
      fieldKey: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  isEditing: PropTypes.bool,
  selectedContact: PropTypes.shape({
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    cell: PropTypes.string,
    notes: PropTypes.string,
    language: PropTypes.string,
    gender: PropTypes.string,
    unit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    units: PropTypes.array,
    Org: PropTypes.shape({
      _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
    Organization: PropTypes.shape({
      _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    }),
  }),
  isInContactPage: PropTypes.bool,
  col: PropTypes.string,
  dialogValue: PropTypes.shape({
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    phone: PropTypes.string,
    cell: PropTypes.string,
    notes: PropTypes.string,
    email: PropTypes.string,
    Org: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Organization: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    unit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    language: PropTypes.string,
    gender: PropTypes.string,
  }),
  setDialogValue: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  setValue: PropTypes.func,
  callback: PropTypes.func,
  selectedOrg: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  popupType: PropTypes.oneOf(Object.values(TrackerDataType)),
  isInAutoTrackerPage: PropTypes.bool,
  contacts: PropTypes.array,
};
